import { useState } from "react";
import { authService, ForgotPasswordResponse } from "../../services/authService";
import { Button, Col, Flex, Form, Input, Row, Spin, Typography } from "antd";
import loginPage from "../login/loginPage.svg";
import XdLogo from "../../assets/XdLogo.svg";
import styles from "./forgot-password.module.scss";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { MailIcon } from "../../assets/icons/MailIcon";

const ForgotPassword: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [ forgotPasswordData, setForgotPasswordData ] = useState<ForgotPasswordResponse | null>(null);
    const [form] = Form.useForm();
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await authService.forgotPasswordRequest(form.getFieldsValue());
            setForgotPasswordData(response);
            console.log(forgotPasswordData);
            setIsSuccess(true);
        } catch (err: any) {
            setError(
                err.response?.data
                    ? JSON.stringify(err.response.data, null, 2)
                    : err.message,
            );
            setIsSuccess(false);
        } finally {
            setLoading(false);
        }
    };


    return (
        <>
            <Spin spinning={loading} fullscreen />
            {isSuccess ? (
                    <Row>
                    <Col xs={24} md={13} lg={13} className={styles.forgotPasswordPage}>
                        <div className={styles.pageHeader}>
                            <span className={styles.xdLogo}>
                                <img
                                    src={XdLogo}
                                    alt="icon"
                                />
                            </span>
                        </div>
                        <div className={styles.centerWrapper}>
                            <div className={styles.successBlock}>
                                <div className={styles.successRow}>
                                    <Typography.Title level={3} className={styles.successText}>
                                        <MailIcon />
                                    </Typography.Title>
                                    <Typography.Title level={3} className={styles.successText}>
                                        Check your email
                                    </Typography.Title>
                                </div>
                                <Typography.Text style={{ color: '#99A0A7', fontWeight: 400, fontSize: '16px' }}>
                                    If an account exists for <Typography.Link className={styles.backToText} style={{ fontWeight: 700 }}>{form.getFieldValue('email')}</Typography.Link>, you'll receive a password reset link shortly.
                                </Typography.Text>
                                <Typography.Title level={5} className={styles.backToText}>
                                    <Typography.Link href="/Login" style={{ fontWeight: 700 }}>
                                        <span className={styles.backLinkContent}>
                                            <FontAwesomeIcon icon={faArrowLeft} /><span>Back to Login</span>
                                        </span>
                                    </Typography.Link>
                                </Typography.Title>
                            </div>
                        </div>
                    </Col>
                    <Col
                        xs={24}
                        md={11}
                        lg={11}
                        style={{
                        height: "100vh",
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "flex-start",
                        }}
                    >
                        <img
                        src={loginPage}
                        alt="icon"
                        style={{
                            display: "block",
                            height: "calc(100vh - 40px)",
                            width: "auto",
                            maxWidth: "calc(100% - 40px)",
                            margin: "20px",
                            borderRadius: "16px",
                            objectFit: "contain",
                        }}
                        />
                    </Col>
                </Row>
            ) : (
                <Row>
                    <Col xs={24} md={13} lg={13} className={styles.forgotPasswordPage}>
                        <div className={styles.pageHeader}>
                            <span className={styles.xdLogo}>
                                <img
                                    src={XdLogo}
                                    alt="icon"
                                />
                            </span>
                        </div>
                        <div className={styles.centerWrapper}>
                            <div className={styles.forgotPasswordForm}>
                                <div className={styles.headerRow}>
                                    <Typography.Title level={3} className={styles.headerText}>
                                        Forgot Password
                                    </Typography.Title>
                                </div>
                                <Typography.Text className={styles.descriptionText} style={{ color: '#99A0A7', fontWeight: 400, fontSize: '16px' }}>
                                    Enter your email address and we'll send you a link to reset your password.
                                </Typography.Text>
                                <Form form={form} onFinish={handleSubmit}>
                                    <Typography.Title level={5} className={styles.inputTitle}>Email</Typography.Title>
                                    <Form.Item shouldUpdate noStyle>
                                        {() => {
                                            const hasEmailError = form.getFieldError('email').length > 0 || !!error;
                                            return (
                                                <Form.Item
                                                    name="email"
                                                    validateTrigger={[]}
                                                    style={{ marginBottom: 16 }}
                                                    rules={[
                                                        {
                                                            type: 'email',
                                                            message: 'Enter a valid email address.',
                                                        },
                                                        {
                                                            required: true,
                                                            message: 'Email address is required.',
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        status={hasEmailError ? 'error' : ''}
                                                        placeholder="Enter Account Email"
                                                        onChange={(e) => {
                                                            setError(null);
                                                            form.setFields([{ name: 'email', errors: [] }]);
                                                        }}
                                                        className={`${styles.emailInput} ${hasEmailError ? styles.emailInputError : ''}`}
                                                    />
                                                </Form.Item>
                                            );
                                        }}
                                    </Form.Item>

                                    {error && (
                                        <Typography.Text className={styles.errorText}><FontAwesomeIcon icon={faCircleXmark} /> Incorrect email. Please try again.</Typography.Text>
                                    )}
                                    
                                    <Flex vertical gap="small" style={{ width: '100%' }}>
                                        <Form.Item shouldUpdate>
                                            {() => {
                                                const hasErrors = form
                                                    .getFieldsError()
                                                    .some(({ errors }) => errors.length);

                                                const isTouched = form.isFieldsTouched(true);

                                                return (
                                                    <Button
                                                        type="primary"
                                                        block
                                                        htmlType="submit"
                                                        disabled={!isTouched || hasErrors}
                                                        className={styles.submitButton}
                                                        style={{ height: "36px", fontWeight: 700, borderRadius: 10 }}
                                                    >
                                                        Send Reset Link
                                                    </Button>
                                                );
                                            }}
                                        </Form.Item>
                                    </Flex>
                                </Form>
                                <Typography.Title level={5} className={styles.contactText}>
                                    <Typography.Link href="/Login" style={{ fontWeight: 700 }}>
                                        <span className={styles.backLinkContent}>
                                            <FontAwesomeIcon icon={faArrowLeft} /><span>Back to Login</span>
                                        </span>
                                    </Typography.Link>
                                </Typography.Title>
                            </div>
                        </div>
                    </Col>
                    <Col
                        xs={24}
                        md={11}
                        lg={11}
                        style={{
                        height: "100vh",
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "flex-start",
                        }}
                    >
                        <img
                        src={loginPage}
                        alt="icon"
                        style={{
                            display: "block",
                            height: "calc(100vh - 40px)",
                            width: "auto",
                            maxWidth: "calc(100% - 40px)",
                            margin: "20px",
                            borderRadius: "16px",
                            objectFit: "contain",
                        }}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};

export default ForgotPassword;
